import { useGetUserProfile } from 'hooks/queries/use-get-user-profile.hook';
import {
  PATIENT_PORTAL_SYSTEM_IDS,
  PatientPortalSystemOptions,
} from 'types/patient-portal-system.types';
import {
  CONSENT_FLAGS,
  MARKETING_COMMS_FLAG,
  MOBILE_VERIFIED_FLAG,
} from './onboarding-account-setup.constants';

type UserProfileData = ReturnType<typeof useGetUserProfile>['data'];

export interface OnboardingStepFlags {
  shouldRenderTermsStep: boolean;
  shouldRenderMarketingStep: boolean;
  shouldRenderPhoneNumberStep: boolean;
  shouldRenderPersonalInformationStep: boolean;
}

export const getOnboardingStepFlags = (
  data: UserProfileData,
  systemId: PatientPortalSystemOptions,
): OnboardingStepFlags => {
  const isAmplar = systemId === PATIENT_PORTAL_SYSTEM_IDS.AMPLAR;
  const userConsent = data?.customFields?.find(
    (u) => u.name === CONSENT_FLAGS[systemId],
  );
  const userMobileVerified = data?.customFields?.find(
    (u) => u.name === MOBILE_VERIFIED_FLAG,
  );
  const userMarketingOptIn = data?.customFields?.find(
    (u) => u.name === MARKETING_COMMS_FLAG,
  );

  return {
    shouldRenderTermsStep: userConsent?.value === undefined,
    shouldRenderMarketingStep: userMarketingOptIn?.value === null && isAmplar,
    shouldRenderPhoneNumberStep:
      userMobileVerified?.value !== 'true' || !isAmplar,
    shouldRenderPersonalInformationStep: !isAmplar,
  };
};

export const isOnboardingAccountSetupComplete = (
  data: UserProfileData,
  systemId: PatientPortalSystemOptions,
): boolean => {
  // The myhealth flow has no save points: every step in
  // OnboardingAccountSetup must be completed in a single sitting before
  // any progress is recorded upstream. The final
  // PersonalInfoReviewStep submission is the only place that writes
  // `myhealth_consent`, so its presence is the reliable "user got all
  // the way to the end at least once" marker.
  if (systemId === PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH) {
    const userConsent = data?.customFields?.find(
      (u) => u.name === CONSENT_FLAGS[systemId],
    );
    return userConsent?.value === 'true';
  }

  // The amplar flow records progress per-step (consent, marketing
  // preference, mobile verification each set their own flag), so
  // completion is just "no step left to render".
  const flags = getOnboardingStepFlags(data, systemId);
  return (
    !flags.shouldRenderTermsStep &&
    !flags.shouldRenderMarketingStep &&
    !flags.shouldRenderPhoneNumberStep
  );
};
